import React, { useEffect, useState } from 'react';
import { viabilidadeAPI } from '@/lib/api';
import { Loader2, Search, MapPin, Hash, Building2 } from 'lucide-react';
import { ConectorAviso } from '@/components/ConectorAviso';

export const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];
export const MODOS_BUSCA = [['cep', 'CEP', MapPin], ['rua', 'Nome da rua', Search], ['hp', 'HP', Hash]];

const cls = 'h-8 rounded-md border bg-white dark:bg-gray-900 px-2 text-xs w-full';
const cacheCidades = {};

// Seletor "Buscar por": CEP | Nome da rua | HP
export const ModoBusca = ({ value, onChange, testid }) => (
  <div className="inline-flex rounded-md overflow-hidden border text-[11px]" data-testid={testid}>
    {MODOS_BUSCA.map(([v, l, Ic]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
        className={`inline-flex items-center gap-1 px-2.5 py-1 font-semibold ${value === v ? 'bg-purple-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50'}`}>
        <Ic className="w-3 h-3" /> {l}
      </button>
    ))}
  </div>
);

export const UfSelect = ({ value, onChange, testid }) => (
  <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testid}>
    <option value="">UF</option>
    {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
  </select>
);

// Cidade com autocomplete das cidades COM CABO da Claro (cache por UF)
export const CidadeClaroInput = ({ uf, value, onChange, testid, placeholder = 'Cidade' }) => {
  const [cidades, setCidades] = useState(cacheCidades[uf] || []);
  const [carregando, setCarregando] = useState(false);
  useEffect(() => {
    if (!uf || uf.length !== 2) { setCidades([]); return undefined; }
    if (cacheCidades[uf]) { setCidades(cacheCidades[uf]); return undefined; }
    let ativo = true;
    setCarregando(true);
    viabilidadeAPI.cidades(uf).then((r) => {
      if (!ativo) return;
      const lista = r.data?.status === 'ok' ? r.data.cidades : [];
      if (lista.length) cacheCidades[uf] = lista;
      setCidades(lista);
    }).catch(() => { if (ativo) setCidades([]); }).finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [uf]);
  const listId = `${testid}-lista`;
  return (
    <div className="relative">
      <input list={listId} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${cls} uppercase`} autoComplete="off" data-testid={testid} />
      <datalist id={listId}>{cidades.map((c) => <option key={c.codigo} value={c.nome} />)}</datalist>
      {carregando && <Loader2 className="w-3 h-3 animate-spin absolute right-2 top-2.5 text-gray-400" />}
    </div>
  );
};

const Botao = ({ onClick, carregando, children, testid }) => (
  <button type="button" onClick={onClick} disabled={carregando} className="h-8 inline-flex items-center gap-1 rounded-md bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white px-3 text-xs font-semibold" data-testid={testid}>
    {carregando ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} {children}
  </button>
);

// Busca pelo nome da rua: UF + cidade + nome -> ruas da Claro -> número -> complementos (mesmo formato de /complementos)
export const BuscaPorRua = ({ uf, cidade, onUf, onCidade, onResultado, origem = 'tela', testid = 'busca-rua' }) => {
  const [nome, setNome] = useState('');
  const [numero, setNumero] = useState('');
  const [ruas, setRuas] = useState(null);
  const [ruaSel, setRuaSel] = useState('');
  const [carregando, setCarregando] = useState('');
  const [aviso, setAviso] = useState(null);
  // mudou UF/cidade/nome → a lista de ruas anterior não vale mais
  useEffect(() => { setRuas(null); setRuaSel(''); }, [uf, cidade, nome]);

  const buscarRuas = async () => {
    if (!uf || !cidade) { setAviso({ status: 'erro', mensagem: 'Informe UF e cidade' }); return; }
    if (nome.trim().length < 3) { setAviso({ status: 'erro', mensagem: 'Digite pelo menos 3 letras do nome da rua' }); return; }
    setCarregando('ruas'); setAviso(null); setRuas(null); setRuaSel('');
    try {
      const r = (await viabilidadeAPI.logradouros({ uf, cidade, logradouro: nome.trim(), origem })).data;
      if (r.status === 'ok') { setRuas(r); if (r.logradouros.length === 1) setRuaSel(r.logradouros[0].codigo); } else setAviso(r);
    } catch { setAviso({ status: 'erro', mensagem: 'Falha ao buscar as ruas na Claro' }); }
    finally { setCarregando(''); }
  };

  const buscarHps = async () => {
    const rua = ruas?.logradouros.find((l) => l.codigo === ruaSel);
    if (!rua) { setAviso({ status: 'erro', mensagem: 'Escolha a rua na lista' }); return; }
    if (!numero.replace(/\D/g, '')) { setAviso({ status: 'erro', mensagem: 'Informe o número' }); return; }
    setCarregando('hps'); setAviso(null);
    try {
      const r = (await viabilidadeAPI.hps({ uf: ruas.cidade.uf, cidade: ruas.cidade.nome, cidade_codigo: ruas.cidade.codigo, logradouro_codigo: rua.codigo, logradouro: rua.logradouro, tipo: rua.tipo, bairro: rua.bairro, numero, origem })).data;
      if (r.status === 'ok') onResultado(r); else setAviso(r);
    } catch { setAviso({ status: 'erro', mensagem: 'Falha ao buscar os complementos na Claro' }); }
    finally { setCarregando(''); }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="grid grid-cols-[70px_1fr] sm:grid-cols-[70px_1fr_1.4fr_auto] gap-2">
        <UfSelect value={uf} onChange={(v) => { onUf(v); onCidade(''); }} testid={`${testid}-uf`} />
        <CidadeClaroInput uf={uf} value={cidade} onChange={onCidade} testid={`${testid}-cidade`} />
        <input value={nome} onChange={(e) => setNome(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && buscarRuas()} placeholder="Nome da rua (ex.: Manuel Caldeira)" className={`${cls} col-span-2 sm:col-span-1`} data-testid={`${testid}-nome`} />
        <Botao onClick={buscarRuas} carregando={carregando === 'ruas'} testid={`${testid}-buscar`}>Buscar rua</Botao>
      </div>
      {ruas && (
        <div className="grid grid-cols-[1fr_90px_auto] gap-2 items-center">
          <select value={ruaSel} onChange={(e) => setRuaSel(e.target.value)} className={cls} data-testid={`${testid}-select`}>
            <option value="">{ruas.total} rua(s) encontrada(s) — escolha…</option>
            {ruas.logradouros.map((l) => <option key={l.codigo} value={l.codigo}>{l.tipo ? `${l.tipo} ` : ''}{l.logradouro}{l.bairro ? ` — ${l.bairro}` : ''}{l.cep ? ` (CEP ${l.cep})` : ''}</option>)}
          </select>
          <input value={numero} onChange={(e) => setNumero(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && buscarHps()} placeholder="Nº" inputMode="numeric" className={cls} data-testid={`${testid}-numero`} />
          <Botao onClick={buscarHps} carregando={carregando === 'hps'} testid={`${testid}-buscar-hps`}>Complementos</Botao>
        </div>
      )}
      <ConectorAviso r={aviso} testid={`${testid}-aviso`} />
    </div>
  );
};

// Busca direta pelo HP: UF + cidade + HP -> serviços + endereço do HP (mesmo formato de /detalhe)
export const BuscaPorHp = ({ uf, cidade, onUf, onCidade, onResultado, origem = 'tela', testid = 'busca-hp' }) => {
  const [hp, setHp] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const buscar = async () => {
    if (!uf || !cidade) { setAviso({ status: 'erro', mensagem: 'Informe UF e cidade' }); return; }
    if (hp.replace(/\D/g, '').length < 5) { setAviso({ status: 'erro', mensagem: 'Informe o número do HP' }); return; }
    setCarregando(true); setAviso(null);
    try {
      const r = (await viabilidadeAPI.detalhe({ hp: hp.replace(/\D/g, ''), uf, cidade, origem, busca: 'hp' })).data;
      if (r.status === 'ok') onResultado(r); else setAviso(r);
    } catch { setAviso({ status: 'erro', mensagem: 'Falha ao consultar o HP na Claro' }); }
    finally { setCarregando(false); }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="grid grid-cols-[70px_1fr] sm:grid-cols-[70px_1fr_1fr_auto] gap-2">
        <UfSelect value={uf} onChange={(v) => { onUf(v); onCidade(''); }} testid={`${testid}-uf`} />
        <CidadeClaroInput uf={uf} value={cidade} onChange={onCidade} testid={`${testid}-cidade`} />
        <input value={hp} onChange={(e) => setHp(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && buscar()} placeholder="Número do HP (ex.: 842096184)" inputMode="numeric" className={`${cls} col-span-2 sm:col-span-1`} data-testid={`${testid}-hp`} />
        <Botao onClick={buscar} carregando={carregando} testid={`${testid}-buscar`}>Buscar HP</Botao>
      </div>
      <ConectorAviso r={aviso} testid={`${testid}-aviso`} />
    </div>
  );
};

// Escolha de UF + cidade (sem CEP) para ver os planos da Claro
export const SeletorCidade = ({ uf, cidade, onUf, onCidade, testid = 'sel-cidade' }) => (
  <div className="grid grid-cols-[70px_1fr] gap-2 max-w-md" data-testid={testid}>
    <UfSelect value={uf} onChange={(v) => { onUf(v); onCidade(''); }} testid={`${testid}-uf`} />
    <div className="relative">
      <CidadeClaroInput uf={uf} value={cidade} onChange={onCidade} testid={`${testid}-cidade`} placeholder="Digite a cidade…" />
      <Building2 className="w-3 h-3 absolute right-6 top-2.5 text-gray-300 pointer-events-none" />
    </div>
  </div>
);
